'use client';

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Badge,
  Card
} from 'react-bootstrap';

import {
  getPharmacyById,
  togglePharmacyStatus,
  approvePharmacy
} from 'features/admin/adminApi';
import { downloadDocument, viewDocument } from 'features/profile/profileApi';

import Button from 'components/base/Button';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faStoreAlt,
  faEnvelope,
  faLocationDot,
  faCheckCircle,
  faTimesCircle,
  faTimes,
  faSpinner,
  faBuildingColumns,
  faFileAlt,
  faPhone,
  faToggleOn,
  faToggleOff,
  faUser,
  faMailBulk
} from '@fortawesome/free-solid-svg-icons';

interface Pharmacy {
  id: number;
  user_id: number;
  name: string;
  last_name?: string;
  email: string;
  pharmacy_name: string;
  phone_number: string;
  dispensing_system: string;
  location: string;
  license_number: string;
  is_active: number;
  is_approved: number;
  created_at: string;
  documents?: any[];
  contact_person?: string;
  address?: string;
}

const AdminPharmacyDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [pharmacy, setPharmacy] = useState<Pharmacy | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchPharmacyDetails();
  }, [id]);

  const fetchPharmacyDetails = async () => {
    try {
      setLoading(true);
      setError('');

      if (!id) {
        throw new Error('Pharmacy ID is required');
      }

      const data = await getPharmacyById(id);
      setPharmacy(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch pharmacy details');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard/admin/user-management/manage-pharmacy');
  };

  const handleToggleStatus = async () => {
    try {
      setActionLoading(true);
      await togglePharmacyStatus(id || '');
      await fetchPharmacyDetails();
      setSuccess('Status updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      await approvePharmacy(id || '');
      await fetchPharmacyDetails();
      setSuccess('Approval status updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update approval status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDocumentView = (doc: any) => {
    viewDocument(doc.document_name);
  };

  const handleDocumentDownload = async (doc: any) => {
    try {
      await downloadDocument(doc.document_name);
      setSuccess('Document downloaded successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(
        err instanceof Error ? err.message : 'Failed to download document'
      );
    }
  };

  if (loading) {
    return (
      <Container className="py-5" fluid>
        <Row className="py-5">
          <Col className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-body-tertiary fw-semibold">
              Loading pharmacy details...
            </p>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!pharmacy) {
    return (
      <Container className="py-5" fluid>
        <Row className="py-5">
          <Col className="text-center">
            <Alert variant="danger">Pharmacy not found</Alert>
            <Button
              variant="phoenix-primary"
              onClick={() =>
                navigate('/dashboard/admin/user-management/manage-pharmacy')
              }
              startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
            >
              Back to Manage Pharmacies
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-2 py-lg-4" fluid>
      {/* Alerts */}
      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              {error}
            </Alert>
          </Col>
        </Row>
      )}
      {success && (
        <Row className="mb-4">
          <Col>
            <Alert variant="success" onClose={() => setSuccess('')} dismissible>
              {success}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Header Section */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-start gap-3">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleBack}
              startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
            >
              Back
            </Button>

            <div className="flex-grow-1">
              <p className="text-body-tertiary text-uppercase fs-15 fw-bold mt-1">
                <FontAwesomeIcon icon={faBuildingColumns} className="me-2" />
                Pharmacy Details
              </p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Key Information Cards */}
      <Row className="mb-4 g-3">
        <Col md={6} lg={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-2">
                <FontAwesomeIcon icon={faStoreAlt} className="me-2" />
                Pharmacy Name
              </p>
              <h6 className="text-body-emphasis mb-0">
                {pharmacy.pharmacy_name || pharmacy.name}
              </h6>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-2">
                <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                License Number
              </p>
              <h6 className="text-body-emphasis mb-0">
                {pharmacy.license_number || 'N/A'}
              </h6>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-2">
                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                Approval
              </p>
              <Badge
                bg={pharmacy.is_approved ? 'success' : 'warning'}
                className="rounded-pill fw-semibold"
              >
                {pharmacy.is_approved ? 'Approved' : 'Pending'}
              </Badge>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body>
              <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-2">
                <FontAwesomeIcon icon={faToggleOn} className="me-2" />
                Status
              </p>
              <Badge
                bg={pharmacy.is_active ? 'success' : 'danger'}
                className="rounded-pill fw-semibold"
              >
                {pharmacy.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Contact Information */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-body-secondary border-bottom border-translucent">
              <h6 className="mb-0 text-body-emphasis">
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                Contact Information
              </h6>
            </Card.Header>
            <Card.Body>
              <Row className="g-4">
                <Col md={6}>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-2">
                    Contact Person
                  </p>
                  <h6 className="text-body-emphasis fw-semibold mb-0">
                    <FontAwesomeIcon icon={faUser} className="me-2" />
                    {pharmacy.name || 'N/A'} {pharmacy.last_name || 'N/A'}
                  </h6>
                </Col>
                <Col md={6}>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-2">
                    Contact Person
                  </p>
                  <h6 className="text-body-emphasis fw-semibold mb-0">
                    <FontAwesomeIcon icon={faUser} className="me-2" />
                    {pharmacy.contact_person || 'N/A'}
                  </h6>
                </Col>
              </Row>
              <Row className="g-4 mt-2">
                <Col md={6}>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-2">
                    Email
                  </p>
                  <h6 className="text-body-emphasis fw-semibold mb-0">
                    <FontAwesomeIcon icon={faMailBulk} className="me-2" />
                    <a
                      href={`mailto:${pharmacy.email}`}
                      className="text-primary text-decoration-none fw-semibold"
                    >
                      {pharmacy.email}
                    </a>
                  </h6>
                </Col>

                <Col md={6}>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-2">
                    Phone Number
                  </p>
                  <h6 className="text-body-emphasis fw-semibold mb-0">
                    <FontAwesomeIcon
                      icon={faPhone}
                      className="me-2 text-danger"
                    />
                    {pharmacy.phone_number || 'N/A'}
                  </h6>
                </Col>
              </Row>

              <Row className="g-4 mt-2">
                <Col md={6}>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-2">
                    Dispensing System
                  </p>
                  <h6 className="text-body-emphasis fw-semibold mb-0">
                    <FontAwesomeIcon icon={faMailBulk} className="me-2" />
                      {pharmacy.dispensing_system || 'N/A'}
                  </h6>
                </Col>

                <Col md={6}>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-2">
                    Address
                  </p>
                  <h6 className="text-body-emphasis fw-semibold mb-0">
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      className="me-2 text-danger"
                    />
                    {pharmacy.address || 'N/A'}
                  </h6>
                </Col>
              </Row>

              <Row className="g-4 mt-2">
                <Col md={6}>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-3">
                    Approval Status
                  </p>
                  <Button
                    variant={
                      pharmacy.is_approved ? 'subtle-warning' : 'subtle-success'
                    }
                    onClick={handleApprove}
                    disabled={actionLoading}
                    startIcon={
                      actionLoading ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <FontAwesomeIcon
                          icon={
                            pharmacy.is_approved ? faTimesCircle : faCheckCircle
                          }
                        />
                      )
                    }
                  >
                    {pharmacy.is_approved
                      ? 'Reject Application'
                      : 'Approve Application'}
                  </Button>
                </Col>

                <Col md={6}>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-3">
                    Account Status
                  </p>
                  <Button
                    variant={
                      pharmacy.is_active ? 'subtle-danger' : 'subtle-success'
                    }
                    onClick={handleToggleStatus}
                    disabled={actionLoading}
                    startIcon={
                      actionLoading ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <FontAwesomeIcon
                          icon={pharmacy.is_active ? faToggleOff : faToggleOn}
                        />
                      )
                    }
                  >
                    {pharmacy.is_active
                      ? 'Deactivate Account'
                      : 'Activate Account'}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Documents Card */}
      {/* <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-body-secondary border-bottom border-translucent">
              <h6 className="mb-0 text-body-emphasis">
                <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                Documents ({pharmacy.documents?.length || 0})
              </h6>
            </Card.Header>
            <Card.Body>
              {pharmacy.documents && pharmacy.documents.length > 0 ? (
                <div className="list-group">
                  {pharmacy.documents.map((doc: any) => (
                    <div
                      key={doc.id}
                      className="list-group-item d-flex justify-content-between align-items-center border-0 border-bottom px-0 py-3"
                    >
                      <div>
                        <h6 className="mb-1 d-flex align-items-center text-body-emphasis">
                          <FontAwesomeIcon
                            icon={faCertificate}
                            className="me-2 text-warning"
                          />
                          {doc.doc_type?.replace(/_/g, ' ').toUpperCase() ||
                            doc.document_name}
                        </h6>
                        <small className="text-body-secondary">
                          {doc.document_name}
                        </small>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="info"
                          size="sm"
                          startIcon={<FontAwesomeIcon icon={faEye} />}
                          onClick={() => handleDocumentView(doc)}
                        >
                          View
                        </Button>
                        <Button
                          variant="phoenix-primary"
                          size="sm"
                          startIcon={<FontAwesomeIcon icon={faDownload} />}
                          onClick={() => handleDocumentDownload(doc)}
                        >
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert variant="info" className="mb-0">
                  No documents uploaded yet
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row> */}

      {/* Additional Information */}
      {/* <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-body-secondary border-bottom border-translucent">
              <h6 className="mb-0 text-body-emphasis">Additional Information</h6>
            </Card.Header>
            <Card.Body>
              <Row className="g-4">
                <Col md={6}>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-2">Pharmacy ID</p>
                  <p className="text-body mb-0 font-monospace fw-semibold">{pharmacy.id}</p>
                </Col>

                <Col md={6}>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-2">User ID</p>
                  <p className="text-body mb-0 font-monospace fw-semibold">{pharmacy.user_id}</p>
                </Col>

                <Col md={6}>
                  <p className="text-body-tertiary fs-10 fw-bold text-uppercase mb-2">Created At</p>
                  <p className="text-body mb-0">
                    {new Date(pharmacy.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row> */}
    </Container>
  );
};

export default AdminPharmacyDetailsPage;
